import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, Users, Wrench } from "lucide-react";
import { MissingEmployeesResolver } from "./MissingEmployeesResolver";

/**
 * iter210 + iter216 — Employee-coverage reconciliation strip.
 * Chips: Master / Attendance / Payroll / Missing / Valid Missing /
 * Excluded / No Attendance / Rate Missing / Duplicate Identity.
 *
 * The red "Payroll ≠ Master/Attendance" banner now respects the
 * classification list — it only lights up when a Missing-from-Payroll
 * name has NO saved exclusion category. Once every miss is
 * legitimately classified, the strip flips to a green "All differences
 * accounted for" pill.
 */
export function SalaryReconciliationStrip({ token, month, targetCenter, canResolve = false }) {
  const [data, setData] = useState(null);
  const [resolverOpen, setResolverOpen] = useState(false);
  const [bump, setBump] = useState(0);

  const load = useCallback(async () => {
    if (!month || !targetCenter) return;
    try {
      const { data } = await api.post("/salary_reconciliation", {
        token, month, targetCenter,
      });
      setData(data);
    } catch { setData(null); }
  }, [token, month, targetCenter]);

  useEffect(() => { load(); }, [load, bump]);

  if (!data) return null;
  const c = data.counts;
  const unresolved = c.unexplained_missing_from_payroll ?? c.missing_from_payroll;
  const excluded = c.excluded_missing_from_payroll ?? 0;
  const validMissing = c.valid_missing_from_payroll ?? 0;
  const hasIssue = unresolved + c.duplicate_identity + c.salary_rate_missing > 0;
  const allResolved = !hasIssue && (c.missing_from_payroll > 0 || excluded > 0);

  const chip = (label, value, tone = "default", body = null, testid = null) => {
    const cls = tone === "danger"
      ? "bg-red-50 text-red-700 border-red-200"
      : tone === "warn"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : tone === "success"
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-50 text-slate-700 border-slate-200";
    const chipEl = (
      <span className={`px-2 py-0.5 rounded-full border text-[11px] ${cls} cursor-default`}
              data-testid={testid || `recon-chip-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        {label}: <b>{value}</b>
      </span>
    );
    if (!body || value === 0) return chipEl;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="cursor-pointer"
            data-testid={`recon-popover-${label.replace(/\s+/g, "-").toLowerCase()}`}>{chipEl}</button>
        </PopoverTrigger>
        <PopoverContent className="w-80">{body}</PopoverContent>
      </Popover>
    );
  };

  const nameList = (arr) => arr.length === 0 ? (
    <div className="text-xs text-muted-foreground">Nothing to show.</div>
  ) : (
    <ul className="text-xs max-h-56 overflow-y-auto space-y-0.5">
      {arr.slice(0, 200).map((n) => <li key={n}>• {n}</li>)}
      {arr.length > 200 && (
        <li className="text-muted-foreground pt-1">
          + {arr.length - 200} more…
        </li>
      )}
    </ul>
  );

  const missingBody = (
    <div>
      <div className="text-xs font-semibold mb-1 text-red-700 inline-flex items-center gap-1">
        <AlertTriangle className="w-3.5 h-3.5" />
        In Master or Attendance but NOT in Payroll
      </div>
      {nameList(data.missing_from_payroll || [])}
      {canResolve && (
        <Button size="sm" className="w-full mt-2 h-7 text-[11px]"
          onClick={() => setResolverOpen(true)}
          data-testid="recon-open-resolver">
          <Wrench className="w-3.5 h-3.5 mr-1" />
          Resolve / Classify Each Employee
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1"
          data-testid="salary-reconciliation-strip">
      <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
        <Users className="w-3 h-3" />
        Coverage:
      </span>
      {chip("Master", c.master)}
      {chip("Attendance", c.attendance)}
      {chip("Payroll", c.payroll)}
      {chip("Missing from Payroll", c.missing_from_payroll,
             unresolved > 0 ? "danger" : "default",
             missingBody)}
      {c.missing_from_payroll > 0 && chip(
        "Valid Missing", validMissing,
        validMissing > 0 ? "warn" : "default", null,
        "recon-chip-valid-missing")}
      {c.missing_from_payroll > 0 && chip(
        "Excluded/Transferred", excluded,
        excluded > 0 ? "success" : "default", null,
        "recon-chip-excluded")}
      {chip("No Attendance", c.missing_attendance,
             "warn",
             (
               <div>
                 <div className="text-xs font-semibold mb-1 text-amber-800">
                   In Master but no attendance rows for {month}
                 </div>
                 {nameList(data.missing_attendance || [])}
               </div>
             ))}
      {chip("Rate Missing", c.salary_rate_missing,
             c.salary_rate_missing ? "danger" : "default",
             (
               <div>
                 <div className="text-xs font-semibold mb-1 text-red-700">
                   No resolvable salary rate
                 </div>
                 {nameList(data.salary_rate_missing || [])}
               </div>
             ))}
      {chip("Duplicate Identity", c.duplicate_identity,
             c.duplicate_identity ? "danger" : "default",
             (
               <div>
                 <div className="text-xs font-semibold mb-1 text-red-700">
                   Same normalised name appears &gt;1 time in Master
                 </div>
                 {nameList(data.duplicate_identity || [])}
               </div>
             ))}
      {hasIssue && (
        <Badge variant="destructive" className="text-[10px] py-0 px-1.5"
                data-testid="recon-strip-has-issue">
          Payroll ≠ Master/Attendance — resolve before Finalise
        </Badge>
      )}
      {allResolved && (
        <Badge className="bg-emerald-600 text-white text-[10px] py-0 px-1.5"
                data-testid="recon-strip-all-resolved">
          All differences accounted for
        </Badge>
      )}
      {canResolve && c.missing_from_payroll > 0 && (
        <Button size="sm" variant="outline"
          className="h-6 px-2 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-50"
          onClick={() => setResolverOpen(true)}
          data-testid="recon-resolve-btn">
          <Wrench className="w-3 h-3 mr-1" />
          Resolve Missing ({c.missing_from_payroll})
        </Button>
      )}

      {resolverOpen && (
        <MissingEmployeesResolver
          token={token}
          month={month}
          center={targetCenter}
          open={resolverOpen}
          onClose={() => setResolverOpen(false)}
          onSaved={() => setBump(b => b + 1)}
        />
      )}
    </div>
  );
}
